import React, { useEffect, useRef } from 'react'
import { IcClose, IconBack } from '../common/Icons'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { DATE_FORMAT, VIEW } from '../../constant'
import {
  setIsFinishCreditCard,
  setIsFinishCrypto,
  setIsFinishGuestInfo,
  setIsLayoutHasScroll,
} from '../../redux/slices/common'
import clsx from 'clsx'
import { renderDates } from '../../utils'
import moment from 'moment'
import LoadingSection from '../common/LoadingSection'

interface Props {
  children: React.ReactNode
  updateView: (_v: string) => void
}
const Layout: React.FC<Props> = (props) => {
  const layoutRef = useRef<HTMLDivElement>(null)
  const { updateView } = props
  const dispatch = useAppDispatch()
  const { view, isFinishCreditCard, isFinishCrypto, isFinishGuestInfo, isLoading } = useAppSelector(
    (state) => state.common
  )
  const { listingData, guestCount, datesCalendar } = useAppSelector((state) => state.property)

  const handleCheckLayoutScroll = () => {
    const layoutElement = layoutRef.current as HTMLDivElement
    const hasVerticalScrollbar = layoutElement.scrollHeight > layoutElement.clientHeight
    dispatch(setIsLayoutHasScroll(hasVerticalScrollbar))
  }

  useEffect(() => {
    handleCheckLayoutScroll()
  }, [view, isFinishCreditCard, isFinishCrypto])

  useEffect(() => {
    const layoutElement = layoutRef.current as HTMLDivElement
    layoutElement.addEventListener('scroll', handleCheckLayoutScroll)
    return () => {
      layoutElement.removeEventListener('scroll', handleCheckLayoutScroll)
    }
  }, [])

  useEffect(() => {
    const layoutElement = layoutRef.current as HTMLDivElement
    layoutElement.addEventListener('resize', handleCheckLayoutScroll)
    return () => window.removeEventListener('resize', handleCheckLayoutScroll)
  }, [])

  const handleClose = () => {
    if (window.closeDtravelWidget) {
      window.closeDtravelWidget()
    }
  }

  const renderTitle = () => {
    if (view === VIEW.LIST) {
      return 'Choose a listing to book'
    }
    if (view === VIEW.SELECT_DATES) {
      if (datesCalendar) {
        const { startDate, endDate } = datesCalendar
        return renderDates(
          startDate ? moment(startDate).format(DATE_FORMAT) : '',
          endDate ? moment(endDate).format(DATE_FORMAT) : '',
          'Select dates'
        )
      }
      return 'Select dates'
    }
    if (view === VIEW.SELECT_GUEST) {
      const count = guestCount.adults + guestCount.children + guestCount.infants
      return count > 0 ? `${count} ${count > 1 ? 'guests' : 'guest'}` : 'Select guests'
    }
    return listingData?.externalName || 'Choose a listing to book'
  }

  const renderBgColorTimeline = (item: number) => {
    if (view === VIEW.DETAIL) {
      return item === 1 ? 'dtravel-bg-forest-600' : 'dtravel-bg-neutral-400'
    }
    if (view === VIEW.SELECT_PAYMENT) {
      if (item === 4 || item === 5) {
        // return isFinishCreditCard || isFinishCrypto ? 'dtravel-bg-forest-600' : 'dtravel-bg-neutral-400'
        return isFinishGuestInfo ? 'dtravel-bg-forest-600' : 'dtravel-bg-neutral-400'
      }
      return 'dtravel-bg-forest-600'
    }
    return 'dtravel-bg-forest-600'
  }

  const handleClickBack = () => {
    if ([VIEW.SELECT_DATES, VIEW.SELECT_GUEST].includes(view)) {
      updateView(VIEW.DETAIL)
    }
    if (view === VIEW.SELECT_PAYMENT) {
      if (isFinishCreditCard) {
        dispatch(setIsFinishCreditCard(false))
      } else if (isFinishCrypto) {
        dispatch(setIsFinishCrypto(false))
      } else {
        if (isFinishGuestInfo) {
          dispatch(setIsFinishGuestInfo(false))
        } else {
          updateView(VIEW.DETAIL)
        }
      }
    }
  }

  return (
    <div ref={layoutRef} id={'dtravelWidgetLayout'} className="dtravel-layout shadow-2xl">
      <LoadingSection isLoading={isLoading} />

      {/*---Header---*/}
      <div className={clsx('dtravel-layout-header', view === VIEW.BOOKING_SUCCESSED ? 'hidden' : '')}>
        {[VIEW.SELECT_DATES, VIEW.SELECT_GUEST, VIEW.SELECT_PAYMENT].includes(view) ? (
          <button onClick={handleClickBack}>
            <IconBack />
          </button>
        ) : (
          <div />
        )}

        <div className={'dtravel-header-content'}>
          <h2
            className={
              'dtravel-inter-500 dtravel-text-16-24 dtravel-neutral-900 dtravel-line-clamp-1 dtravel-text-center'
            }
          >
            {renderTitle()}
          </h2>

          {/* ---Timeline----*/}
          {[VIEW.DETAIL, VIEW.SELECT_PAYMENT].includes(view) && (
            <div className={'dtravel-center-block'}>
              {[1, 2, 3, 4, 5].map((item) => {
                if (item % 2 === 1) {
                  return (
                    <div
                      key={item}
                      className={`dtravel-timeline-value ${renderBgColorTimeline(item)} dtravel-rounded-full`}
                    />
                  )
                }
                return <div key={item} className={`dtravel-timeline-line ${renderBgColorTimeline(item)}`} />
              })}
            </div>
          )}
        </div>
        <button onClick={handleClose}>
          <IcClose />
        </button>
      </div>
      <div>{props.children}</div>
    </div>
  )
}

export default Layout
